import { Entity, Plan } from './entity';
import { PracticeRegionReservation } from './practice-region-reservation';
import { Center } from './center';
import { SetSuppliesReservation } from './set-supplies-reservation';
import { ReservationSlot } from './reservation-slot';
import { isValidMedicalOrderDate } from '../validators/medical-order-edit.validator';
import { ReferringPhysicianFilter } from './referring-physician/referring-physician-filter';
import { MedicalAppointmentValidation } from './medical-appointment-validation';

export interface ServiceReservationInfo {
  MedicalAppointmentWaitId?: number;
  MedicalAppointmentId?: number;
  DateOrder?: Date;
  DateAuthorization?: Date;
  Diagnostic: string;
  Observation: string;
  PatientObservation: string;
  CredentialNumber: string;
  SecurityCodeCredentialNumber: string;
  ReferringPhysician: ReferringPhysicianFilter;
  Index: number;
  Center: Center;
  Entity: Entity;
  Plan: Plan;
  ReservationSlot: ReservationSlot;
  PracticeRegionReservation: PracticeRegionReservation;
  PracticesRegionReservation: PracticeRegionReservation[];
  PracticesRegionAditionalReservation: PracticeRegionReservation[];
  SetSuppliesReservation: SetSuppliesReservation[];
  MedicalAppointmentOriginalId?: number;
  ReAppointmentDuration?: number;
  MedicalAppointmentValidation: MedicalAppointmentValidation;
 }

export class ServiceReservation {

  constructor(serviceReservationInfo: ServiceReservationInfo = null) {
    if (serviceReservationInfo) {
      this.ReferringPhysician = serviceReservationInfo.ReferringPhysician;
      this.Diagnostic = serviceReservationInfo.Diagnostic;
      this.Observation = serviceReservationInfo.Observation;
      this.PatientObservation = serviceReservationInfo.PatientObservation;
      this.CredentialNumber = serviceReservationInfo.CredentialNumber;
      this.SecurityCodeCredentialNumber = serviceReservationInfo.SecurityCodeCredentialNumber;
      
      if (serviceReservationInfo.DateOrder) {
        this.DateOrder = new Date(serviceReservationInfo.DateOrder);
      }

      if (serviceReservationInfo.DateAuthorization) {
        this.DateAuthorization = new Date(serviceReservationInfo.DateAuthorization);
      }
      this.MedicalAppointmentWaitId = serviceReservationInfo.MedicalAppointmentWaitId;
      this.MedicalAppointmentId = serviceReservationInfo.MedicalAppointmentId;
      this.MedicalAppointmentOriginalId = serviceReservationInfo.MedicalAppointmentOriginalId;
      this.ReAppointmentDuration = serviceReservationInfo.ReAppointmentDuration;
      this.Center = serviceReservationInfo.Center;
      this.Entity = serviceReservationInfo.Entity;
      this.Plan = serviceReservationInfo.Plan;
      this.PracticesRegionReservation = serviceReservationInfo.PracticesRegionReservation;
      this.PracticesRegionAditionalReservation = serviceReservationInfo.PracticesRegionAditionalReservation;
      this.SetSuppliesReservation = serviceReservationInfo.SetSuppliesReservation;
      this.PracticeRegionReservation = serviceReservationInfo.PracticeRegionReservation;
      this.Index = serviceReservationInfo.Index;
    } else {
      this.PracticesRegionReservation = [];
      this.PracticesRegionAditionalReservation = [];
      this.SetSuppliesReservation = [];
      this.Index = 0;
    }
  }

  public MedicalAppointmentWaitId?: number;
  public MedicalAppointmentId?: number;
  public MedicalAppointmentOriginalId?: number;
  public ReAppointmentDuration?: number;
  public DateAuthorization?: Date;
  public DateOrder: Date;
  public Diagnostic: string;
  public Observation: string;
  public PatientObservation: string;
  public CredentialNumber: string;
  public SecurityCodeCredentialNumber: string;
  public Index: number;
  public Center: Center;
  public Entity: Entity;
  public Plan: Plan;
  public ReferringPhysician: ReferringPhysicianFilter;
  public ReservationSlot: ReservationSlot;
  public PracticeRegionReservation: PracticeRegionReservation;
  public PracticesRegionReservation: PracticeRegionReservation[];
  public PracticesRegionAditionalReservation: PracticeRegionReservation[];
  public SetSuppliesReservation: SetSuppliesReservation[];
  public MedicalAppointmentValidation: MedicalAppointmentValidation;

  public diagnosticRequired(): boolean {

    let diagnosticRequired = false;
    if (this.PracticesRegionReservation != null &&
          this.PracticesRegionReservation.length > 0) {
      this.PracticesRegionReservation.forEach((item: PracticeRegionReservation) => {
        if (!diagnosticRequired && item.DiagnosticRequired) {
          diagnosticRequired = true;
        }
      });
      return diagnosticRequired;
    }
  }

  public serviceId(): number {
    return this.PracticesRegionReservation != null
      && this.PracticesRegionReservation.length > 0 ?
      this.PracticesRegionReservation[0].ServiceId : 0;
  }

  public principalPracticeRegionReservationName(): string {
    return this.PracticesRegionReservation != null
      && this.PracticesRegionReservation.length > 0 ?
      this.PracticesRegionReservation[0].Name : '';
  }

  public principalPracticeRegionServiceColor(): string {
    return this.PracticesRegionReservation != null
      && this.PracticesRegionReservation.length > 0 ?
      this.PracticesRegionReservation[0].ServiceColor : '';
  }

  public allPracticesRegionIds(): number[] {
    const ids: number[] = [];
    if (this.PracticesRegionReservation != null
      && this.PracticesRegionReservation.length > 0) {
      this.PracticesRegionReservation.forEach((item: PracticeRegionReservation) => {
        ids.push(item.PracticeRegion.Id);
      });
    }
    return ids;
  }

  public allPracticesAndAditionalRegionIds(): number[] {
    const ids: number[] = [];
    if (this.PracticesRegionReservation != null && this.PracticesRegionReservation.length > 0) {
      this.PracticesRegionReservation.forEach((item: PracticeRegionReservation) => {
        ids.push(item.PracticeRegion.Id);
      });
    }

    if (this.PracticesRegionAditionalReservation != null && this.PracticesRegionAditionalReservation.length > 0) {
      this.PracticesRegionAditionalReservation.forEach((item: PracticeRegionReservation) => {
        if (item.Select) {
          ids.push(item.PracticeRegion.Id);
        }
      });
    }
    return ids;
  }

  public isAthorizationValidity(): boolean {
    return this.Entity.AthorizationValidity;
  }

  public isValidaDate(): boolean {
    const date = this.Entity.AthorizationValidity ? this.DateAuthorization : this.DateOrder;
    const validityDay = this.Entity.AthorizationValidity ?  this.Entity.AthorizationValidityDay
     : this.Entity.MedicalOrderValidityDay;
    if (this.Entity.AthorizationValidity === true || this.Entity.MedicalOrderValidity === true) {
      return isValidMedicalOrderDate(date, validityDay);
     } else {
      return true;
    }
  }

  public hasDate(): boolean {
    const date = this.Entity.AthorizationValidity ? this.DateAuthorization : this.DateOrder;
    return date !== null && date !== undefined ;
  }
}
